import * as THREE from 'three';

/** Overall cue length (metres) and the radii at the tip and butt ends. */
export const STICK_LENGTH = 1.45;
const TIP_RADIUS = 0.007;
const BUTT_RADIUS = 0.012;
/** Length of the pale ferrule near the tip and the dark butt cap, metres. */
const FERRULE_LENGTH = 0.04;
const BUTT_CAP_LENGTH = 0.12;
/** Max distance the stick is drawn back along -aimDir at pullback = 1, metres. */
const MAX_PULLBACK = 0.25;
/** Resting gap between the tip and the cue ball while aiming (~3 inches), metres.
 * The cue sits back off the ball before the stroke, as a real player addresses it. */
const TIP_GAP = 0.076;

/**
 * Cue stick mesh for aiming and shot telegraphing (z-up world). The stick is
 * built pointing along +X with its TIP at the local origin and the butt at
 * -STICK_LENGTH on X, so posing reduces to orienting +X along the aim direction
 * and dropping the group's origin at the desired tip contact point.
 *
 * `pose` places the tip against the cue ball on the side opposite the aim
 * direction (so the player strikes through the ball), tilts the cue up by the
 * given elevation, and slides it back along -aimDir for power.
 */
/** Chalk cube size (metres) and how long one chalking gesture lasts (seconds). */
const CHALK_SIZE = 0.024;
const CHALK_DURATION = 1.2;
/** Resting distance the chalk's centre sits ahead of the tip while rubbing. */
const CHALK_HOLD_X = TIP_RADIUS + CHALK_SIZE / 2 + 0.002;

export class CueStick {
  readonly group = new THREE.Group();
  private readonly ballRadius: number;
  // play-boundary (cushion nose) half-extents + rail top height, for clearance
  private halfW = Infinity;
  private halfL = Infinity;
  private railTop = 0;

  // ---- chalking gesture (a blue chalk cube rubbed across the tip on key 'c') ----
  private readonly chalk: THREE.Mesh;
  private readonly dust: THREE.Mesh;
  private chalkActive = false;
  private chalkT = 0;

  // Scratch objects reused every pose() to avoid per-frame allocation.
  private readonly aimDir = new THREE.Vector3();
  private readonly side = new THREE.Vector3();
  private readonly tip = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();
  private readonly quat = new THREE.Quaternion();
  private readonly camRight = new THREE.Vector3();
  private readonly camUp = new THREE.Vector3();
  private readonly camFwd = new THREE.Vector3();
  private readonly camPos = new THREE.Vector3();
  private static readonly LOCAL_AXIS = new THREE.Vector3(1, 0, 0);
  private static readonly UP = new THREE.Vector3(0, 0, 1);

  constructor(ballRadius: number) {
    this.ballRadius = ballRadius;

    const shaftMat = new THREE.MeshStandardMaterial({ color: 0xc8a165, roughness: 0.5, metalness: 0.05 });
    const ferruleMat = new THREE.MeshStandardMaterial({ color: 0xf3ead6, roughness: 0.35, metalness: 0 });
    const buttMat = new THREE.MeshStandardMaterial({ color: 0x2a1c12, roughness: 0.4, metalness: 0.1 });

    // Lay each segment along the cue's +X axis (tip at the local origin, butt at
    // -X) by rotating -90 deg about Z. After this rotation the cylinder's local
    // +Y (radiusTop) maps to world +X, i.e. the tip-ward end of the segment — so
    // radiusTop must be the SMALLER (tip-side) radius for a clean tip→butt taper.
    const RADIAL = 28; // one segment count everywhere so joints line up exactly
    const layAlongX = (mesh: THREE.Mesh, centerX: number) => {
      mesh.rotation.z = -Math.PI / 2;
      mesh.position.x = centerX;
      mesh.castShadow = true;
      this.group.add(mesh);
    };

    // Single continuous taper from tip to butt; radAt(d) is the radius a distance
    // d back from the tip. Adjacent segments share the radius at their joint, so
    // the silhouette and normals are continuous (no bumps).
    const radAt = (d: number) => TIP_RADIUS + (BUTT_RADIUS - TIP_RADIUS) * (d / STICK_LENGTH);
    const shaftStart = FERRULE_LENGTH;
    const shaftEnd = STICK_LENGTH - BUTT_CAP_LENGTH;
    const shaftLen = shaftEnd - shaftStart;

    // Pale ferrule at the tip: TIP_RADIUS (tip side) -> radAt(ferrule end).
    const ferrule = new THREE.Mesh(
      new THREE.CylinderGeometry(TIP_RADIUS, radAt(FERRULE_LENGTH), FERRULE_LENGTH, RADIAL),
      ferruleMat,
    );
    layAlongX(ferrule, -FERRULE_LENGTH / 2);

    // Tapered shaft: continues the taper from the ferrule to the butt.
    const shaft = new THREE.Mesh(
      new THREE.CylinderGeometry(radAt(shaftStart), radAt(shaftEnd), shaftLen, RADIAL),
      shaftMat,
    );
    layAlongX(shaft, -(shaftStart + shaftLen / 2));

    // Dark butt cap at the back of the cue, finishing the taper at BUTT_RADIUS.
    const butt = new THREE.Mesh(
      new THREE.CylinderGeometry(radAt(shaftEnd), BUTT_RADIUS, BUTT_CAP_LENGTH, RADIAL),
      buttMat,
    );
    layAlongX(butt, -(shaftEnd + BUTT_CAP_LENGTH / 2));

    // Chalk cube (held against the tip during the chalking gesture) + a soft dust
    // puff. Both ride the cue group near the local origin (the tip) and stay
    // hidden until startChalk(). The worn rubbing face is a paler blue.
    const chalkMat = new THREE.MeshStandardMaterial({ color: 0x2f6fb0, roughness: 0.95, metalness: 0 });
    const chalkFaceMat = new THREE.MeshStandardMaterial({ color: 0x7fb0e0, roughness: 1, metalness: 0 });
    // Box faces order: +X,-X,+Y,-Y,+Z,-Z — make the -X face (toward the tip) the worn one.
    this.chalk = new THREE.Mesh(new THREE.BoxGeometry(CHALK_SIZE, CHALK_SIZE, CHALK_SIZE), [
      chalkMat, chalkFaceMat, chalkMat, chalkMat, chalkMat, chalkMat,
    ]);
    this.chalk.castShadow = true;
    this.chalk.visible = false;
    this.group.add(this.chalk);

    this.dust = new THREE.Mesh(
      new THREE.SphereGeometry(0.012, 10, 10),
      new THREE.MeshBasicMaterial({ color: 0xbcd6f0, transparent: true, opacity: 0, depthWrite: false }),
    );
    this.dust.visible = false;
    this.group.add(this.dust);

    // A sane default pose so the stick is valid before the first aim update.
    this.pose(new THREE.Vector3(0, 0, ballRadius), 0, 0, { a: 0, b: 0 }, 0);
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
    if (!v) this.cancelChalk(); // hiding the cue ends any chalking in progress
  }

  /** True while the chalking gesture is playing (the caller backs the tip off the
   * ball so the chalk has room). */
  isChalking(): boolean {
    return this.chalkActive;
  }

  /** Begin (or restart) the chalking animation. */
  startChalk(): void {
    this.chalkActive = true;
    this.chalkT = 0;
    this.chalk.visible = true;
  }

  /**
   * Pose the cue held up in front of the camera (tip toward the viewer, butt
   * dropping out of frame) so the chalking gesture reads in first person instead
   * of clipping through the table. Call instead of pose() while chalking.
   */
  poseChalkInView(camera: THREE.Camera): void {
    camera.updateMatrixWorld();
    const e = camera.matrixWorld.elements;
    this.camRight.set(e[0], e[1], e[2]).normalize();
    this.camUp.set(e[4], e[5], e[6]).normalize();
    this.camFwd.set(-e[8], -e[9], -e[10]).normalize(); // camera looks down -Z
    this.camPos.setFromMatrixPosition(camera.matrixWorld);

    // Tip sits just below frame centre, a little to the right, ~0.5 m ahead.
    this.tip
      .copy(this.camPos)
      .addScaledVector(this.camFwd, 0.5)
      .addScaledVector(this.camRight, 0.12)
      .addScaledVector(this.camUp, -0.06);

    // The stick rises up the frame (tip up toward the viewer) with the butt
    // dropping toward the lower corner, foreshortened toward the camera.
    this.forward
      .copy(this.camUp)
      .multiplyScalar(0.92)
      .addScaledVector(this.camFwd, -0.32)
      .addScaledVector(this.camRight, 0.12)
      .normalize();

    this.quat.setFromUnitVectors(CueStick.LOCAL_AXIS, this.forward);
    this.group.quaternion.copy(this.quat);
    this.group.position.copy(this.tip);
  }

  private cancelChalk(): void {
    this.chalkActive = false;
    this.chalk.visible = false;
    this.dust.visible = false;
  }

  /** Advance the chalking gesture; call once per frame with the frame delta (s). */
  updateChalk(dt: number): void {
    if (!this.chalkActive) return;
    this.chalkT += dt;
    if (this.chalkT >= CHALK_DURATION) {
      this.cancelChalk();
      return;
    }
    const p = this.chalkT / CHALK_DURATION; // 0..1 through the gesture
    // ease the chalk in at the start and out at the end so it doesn't pop
    let env = 1;
    if (p < 0.18) env = p / 0.18;
    else if (p > 0.82) env = (1 - p) / 0.18;

    // rub back and forth across the tip face (local Y, with a little Z), pressing
    // in (smaller X) while rubbing and lifting away (larger X) on approach/leave
    const phase = p * Math.PI * 2 * 4; // ~4 strokes
    const rubY = Math.sin(phase) * 0.012 * env;
    const rubZ = Math.cos(phase * 0.5) * 0.004 * env;
    this.chalk.position.set(CHALK_HOLD_X + (1 - env) * 0.03, rubY, rubZ);
    this.chalk.rotation.set(rubZ * 7, 0, rubY * 9); // slight twist of the cube

    // dust puff that swells and fades with the rubbing
    this.dust.visible = true;
    this.dust.position.set(TIP_RADIUS + 0.004, rubY * 0.4, rubZ * 0.4);
    this.dust.scale.setScalar(0.6 + p * 1.6);
    const m = this.dust.material as THREE.MeshBasicMaterial;
    m.opacity = env * 0.28 * (0.55 + 0.45 * Math.abs(Math.sin(phase)));
  }

  /** Table bounds so the cue can auto-lift over the rail instead of clipping it. */
  setBounds(halfW: number, halfL: number, railTop: number): void {
    this.halfW = halfW;
    this.halfL = halfL;
    this.railTop = railTop;
  }

  /**
   * Minimum cue elevation (rad) needed for the shaft to clear the rail behind the
   * cue ball on this aim line. The table physically blocks any lower angle, so
   * callers use it as a hard floor on the player's elevation/spin controls.
   * `tipB` is the vertical spin offset (-1..1): bottom (draw) english lowers the
   * tip contact, dropping the whole shaft, so it needs MORE elevation to clear.
   */
  requiredElevation(cueBallPos: THREE.Vector3, azimuth: number, tipB = 0): number {
    const R = this.ballRadius;
    const ax = Math.cos(azimuth);
    const ay = Math.sin(azimuth);
    const ox = cueBallPos.x - ax * R;
    const oy = cueBallPos.y - ay * R;
    const sh = exitDist(ox, oy, -ax, -ay, this.halfW, this.halfL);
    if (sh > 1e-3 && sh < STICK_LENGTH) {
      const tipZ = cueBallPos.z + clamp(tipB, -1, 1) * R * 0.9; // tip contact height with spin
      return Math.min(1.3, Math.atan2(Math.max(0, this.railTop - tipZ), sh));
    }
    return 0;
  }

  /**
   * Lay the cue flat on the bed for the opening "lever" break: the BUTT is pinned
   * at `pivot` (a table corner) and the shaft extends one cue-length out along the
   * horizontal direction `dir`, so the tip swings around the corner as `dir`
   * rotates. `dir` need not be normalized; a near-zero `dir` keeps the last
   * heading.
   */
  poseLever(pivot: THREE.Vector3, dir: THREE.Vector3): void {
    this.forward.set(dir.x, dir.y, 0);
    if (this.forward.lengthSq() < 1e-8) this.forward.copy(this.aimDir); // reuse last heading
    this.forward.normalize();
    this.aimDir.copy(this.forward); // remember it for the next near-zero swing
    // the group origin is the tip (butt is at local -X), so place the tip a full
    // stick-length out from the pinned butt at the corner
    this.tip.copy(pivot).addScaledVector(this.forward, STICK_LENGTH);
    if (this.tip.z < TIP_RADIUS) this.tip.z = TIP_RADIUS;
    this.quat.setFromUnitVectors(CueStick.LOCAL_AXIS, this.forward);
    this.group.quaternion.copy(this.quat);
    this.group.position.copy(this.tip);
  }

  /**
   * Pose the stick so its tip is just behind the cue ball, pointing along the
   * aim direction, lying mostly along -aimDir (the butt is behind the player).
   * @param cueBallPos  world position of the cue ball centre (z = ballRadius)
   * @param azimuth     aim angle in the x-y plane (radians)
   * @param elevation   cue elevation above horizontal (radians; 0 = level)
   * @param tipOffset   normalized tip contact offset (-1..1): a = side, b = vertical
   * @param pullback    0..1, how far the stick is drawn back for power (0 = tip at ball)
   */
  pose(
    cueBallPos: THREE.Vector3,
    azimuth: number,
    elevation: number,
    tipOffset: { a: number; b: number },
    pullback: number,
  ): void {
    // In-plane aim direction and the in-plane "left" perpendicular (k x aimDir).
    // The player's right is therefore -side, which is where positive a (right
    // English) must place the tip.
    this.aimDir.set(Math.cos(azimuth), Math.sin(azimuth), 0);
    this.side.set(-Math.sin(azimuth), Math.cos(azimuth), 0);

    const R = this.ballRadius;
    const a = clamp(tipOffset.a, -1, 1);
    const b = clamp(tipOffset.b, -1, 1);

    // Auto-lift the cue so it rests over the rail rather than passing through it:
    // elevate at least enough to clear the rail behind the ball (safety net for
    // poses not already floored by the caller, e.g. a remote opponent's cue).
    // Computed first so the tip contact can ride up the ball with the angle.
    const renderE = Math.max(elevation, this.requiredElevation(cueBallPos, azimuth, b));
    const cosE = Math.cos(renderE);
    const sinE = Math.sin(renderE);

    // Tip contact point on the ball surface, OPPOSITE the cue's forward direction
    // so the cue strikes through the ball centre. As the cue elevates the contact
    // rides up and over the ball — from the back at level to the top at vertical —
    // matching a real elevated / massé address instead of poking the back of the
    // ball at the 0° spot.
    //
    // The tip offset (a, b) lands in the disc PERPENDICULAR to the shaft, not in
    // fixed world axes: side English `a` runs along `side` (always horizontal, so
    // it's already perpendicular), while vertical English `b` runs along the
    // "cue-up" axis = forward rotated 90° in the aim-vertical plane
    // (aimDir·sinE + up·cosE). At level that's straight up (ordinary follow/draw);
    // at a vertical 90° cue it's the aim direction, so moving the dot to "top"
    // slides the contact ACROSS the top of the ball instead of lifting it higher.
    // Positive a = right English → contact on the player's right (-side).
    this.tip.copy(cueBallPos)
      .addScaledVector(this.aimDir, -R * cosE)
      .addScaledVector(CueStick.UP, R * sinE)
      .addScaledVector(this.side, -a * R * 0.9)
      .addScaledVector(this.aimDir, b * R * 0.9 * sinE)
      .addScaledVector(CueStick.UP, b * R * 0.9 * cosE);

    // The cue points forward along aimDir and tilts DOWN toward the ball as the
    // elevation rises, so the butt lifts up behind the player (a real elevated
    // cue / massé pose). The butt is at local -X, hence the negative z here.
    this.forward.set(this.aimDir.x * cosE, this.aimDir.y * cosE, -sinE);

    // Pull the whole stick back along the (tilted) shot line to telegraph power.
    const draw = clamp(pullback, 0, 1) * MAX_PULLBACK;
    this.tip.addScaledVector(this.forward, -(TIP_GAP + draw));

    // Keep the tip above the cloth so the cue never dives into the bed.
    if (this.tip.z < TIP_RADIUS) this.tip.z = TIP_RADIUS;

    // Orient local +X (tip->butt is local -X, so local +X is the shot direction)
    // onto `forward`, then place the group origin at the tip.
    this.quat.setFromUnitVectors(CueStick.LOCAL_AXIS, this.forward);
    this.group.quaternion.copy(this.quat);
    this.group.position.copy(this.tip);
  }
}

/** Clamp `x` to the inclusive range [lo, hi]. */
function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/** Distance from (ox,oy) along (dx,dy) to the box |x|=hw, |y|=hl (Infinity = none). */
function exitDist(ox: number, oy: number, dx: number, dy: number, hw: number, hl: number): number {
  let s = Infinity;
  if (dx > 1e-6) s = Math.min(s, (hw - ox) / dx);
  else if (dx < -1e-6) s = Math.min(s, (-hw - ox) / dx);
  if (dy > 1e-6) s = Math.min(s, (hl - oy) / dy);
  else if (dy < -1e-6) s = Math.min(s, (-hl - oy) / dy);
  return s;
}
